import type { Ref } from 'vue'
import type { Subscription } from 'rxjs'
import type { Viewer } from '@puppycad/cad-core'
import type { ViewportSelectionSnapshot } from '@/composables/extrude-types'
import {
  registerEditorSelectionSubscriptions,
  type EditorViewportEmptySelectionSource,
  type EditorSelectionSubscriptionViewer,
} from '@/views/editor/registerEditorSelectionSubscriptions'
import {
  useEditorImportedModelSelectionSync,
  type ImportedModelSelectionBindingLookup,
} from '@/views/editor/useEditorImportedModelSelectionSync'

export type EditorViewportInteractionSyncViewer =
  EditorSelectionSubscriptionViewer
  & Pick<Viewer, 'buildImportedSelectionSummary'>

export interface UseEditorViewportInteractionSyncOptions {
  /** 获取当前 Viewer；Viewer 是非响应式运行时对象，必须由宿主按生命周期提供。 */
  getViewer: () => EditorViewportInteractionSyncViewer | null
  /** 导入模型对象与设计树节点的映射查询。 */
  glbFeatureTree: ImportedModelSelectionBindingLookup
  /** 设计树选中 key 集合。 */
  selectedKeys: Ref<string[]>
  /** 设计树当前选中对象。 */
  selectedObject: Ref<unknown>
  /** 判断当前模式是否允许把 Viewer 选中态同步回 UI。 */
  canSync?: () => boolean
  /** 判断当前是否处于草图选择会话。 */
  isSketchSelectionMode?: () => boolean
  //------ 草图圆角选择属性面板门禁 开始------
  /** 判断当前是否允许视口选择切换到普通草图实体属性面板。 */
  canShowSketchSelectionProperty?: () => boolean
  //------ 草图圆角选择属性面板门禁 结束------
  /** 编辑态命令工作流需要消费的视口选择快照；只读预览态可省略。 */
  handleViewportSelectionChange?: (selection: ViewportSelectionSnapshot) => void | Promise<void>
  //------ 草图空选显示设计树 开始------
  /** 视口选择结算为空时的处理函数；只读预览态可省略。 */
  handleViewportEmptySelectionChange?: (source: EditorViewportEmptySelectionSource) => void
  //------ 草图空选显示设计树 结束------
}

/**
 * 创建 Editor/版本预览共用的 Viewer 交互同步器。
 *
 * 设计说明：
 * 1. 这里仅收口 imported-model 的选中回流与视口选择事件订阅，不承接任何编辑命令；
 * 2. Editor 传入 `handleViewportSelectionChange` 后继续驱动草图、圆角等命令选择流；
 * 3. VersionPreview 不传命令处理器，只复用高亮、选中与清空逻辑，避免只读页混入编辑态副作用。
 *
 * @param options 同步器依赖。
 * @returns 交互订阅的注册与释放方法。
 */
export function useEditorViewportInteractionSync(options: UseEditorViewportInteractionSyncOptions) {
  const subscriptions: Subscription[] = []
  const importedModelSelectionSync = useEditorImportedModelSelectionSync({
    getViewer: options.getViewer,
    glbFeatureTree: options.glbFeatureTree,
    selectedKeys: options.selectedKeys,
    selectedObject: options.selectedObject,
    canSync: options.canSync,
  })

  /**
   * 注册当前 Viewer 的实体交互订阅。
   *
   * 设计说明：注册前先释放旧订阅，确保快速重建 Viewer 或重复 ready 时不会产生重复回调。
   */
  function register(): void {
    dispose()
    const viewer = options.getViewer()
    if (!viewer) {
      return
    }

    subscriptions.push(...registerEditorSelectionSubscriptions({
      viewer,
      importedModelSelectionSync,
      handleViewportSelectionChange: options.handleViewportSelectionChange ?? ignoreViewportSelectionChange,
      isSketchSelectionMode: options.isSketchSelectionMode,
      //------ 草图圆角选择属性面板门禁 开始------
      canShowSketchSelectionProperty: options.canShowSketchSelectionProperty,
      //------ 草图圆角选择属性面板门禁 结束------
      //------ 草图空选显示设计树 开始------
      handleViewportEmptySelectionChange: options.handleViewportEmptySelectionChange,
      //------ 草图空选显示设计树 结束------
    }))
  }

  /**
   * 释放当前同步器持有的全部订阅。
   */
  function dispose(): void {
    for (const subscription of subscriptions) {
      subscription.unsubscribe()
    }
    subscriptions.length = 0
  }

  return {
    register,
    dispose,
  }
}

/**
 * 只读预览态不消费命令选择流，因此以空处理器承接 SelectionUpdated。
 */
function ignoreViewportSelectionChange(): void {}
